import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { ApiError } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { BowArc } from '@/components/ui/BowArc';
import { Eyebrow, Display } from '@/components/ui/Typography';
import { friendlyMessage } from '@/lib/errors';
import { formatCPF, formatPhone, unmaskDigits } from '@/lib/masks';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';

/**
 * Tela pós-login Google: user tem conta (via google_sub) mas CPF/phone/consent
 * ainda estão vazios. PagBank PIX e /charging/start são bloqueados no backend
 * (403 profile_incomplete) até o preenchimento.
 *
 * Intencionalmente simples — um formulário; sem sugestões de fluxo ou upsell.
 */
export default function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  // Destino original preservado pelo desvio (Login/Register/RequireAuth) —
  // ex.: QR do aspirador. Sem ele, cai na Home.
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const [phone, setPhone] = useState(formatPhone(user?.phone ?? ''));
  const [cpf, setCpf] = useState(formatCPF(user?.cpf ?? ''));
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { kbOpen, vvHeight } = useKeyboardViewport();

  // Centraliza o campo focado no miolo rolável — o campo seguinte aparece
  // espiando abaixo da dobra do teclado (mesmo fix do Register, 2026-07-04).
  function revealFocusedField(e: React.FocusEvent<HTMLElement>) {
    const el = e.target;
    if (!(el instanceof HTMLElement) || el.tagName !== 'INPUT') return;
    window.setTimeout(() => {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 250);
  }

  // Se backend diz que perfil já está completo, user chegou aqui por engano
  // (cache stale, link direto, etc.). Faz refresh de /me pra confirmar e
  // redireciona — evita UI estranha com form pré-preenchido mas sem ação útil.
  useEffect(() => {
    authApi
      .me()
      .then((fresh) => {
        setUser(fresh);
        if (fresh.profile_complete) {
          navigate(from, { replace: true });
        }
      })
      .catch(() => {
        /* rede offline — user preenche form e tenta submit normal */
      });
  }, [navigate, setUser]);

  const phoneDigits = phone.replace(/\D/g, '').length;
  const cpfDigits = cpf.replace(/\D/g, '').length;
  const canSubmit =
    phoneDigits >= 10 &&
    cpfDigits === 11 &&
    consentTerms &&
    consentPrivacy &&
    !submitting;

  const disabledReasons: string[] = [];
  if (phoneDigits < 10) disabledReasons.push('celular (mínimo 10 dígitos)');
  if (cpfDigits !== 11) disabledReasons.push('CPF (11 dígitos)');
  if (!consentTerms) disabledReasons.push('aceitar os termos');
  if (!consentPrivacy) disabledReasons.push('aceitar a política de privacidade');

  async function onSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      const updated = await authApi.completeProfile({
        phone: unmaskDigits(phone),
        cpf: unmaskDigits(cpf),
        consent_terms: true as const,
        consent_privacy: true as const,
        consent_marketing: consentMarketing
      });
      setUser(updated);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // Discriminação por código — NÃO tratar 409 genérico como "completo"
        // (loop de navegação se for cpf_taken + guard de RequireAuth).
        if (err.code === 'profile_already_complete') {
          navigate(from, { replace: true });
          return;
        }
        if (err.code === 'cpf_taken') {
          setError(
            'Este CPF já está cadastrado em outra conta. Se é sua, ' +
            'faça logout e entre com seu email/senha.'
          );
        } else if (err.code === 'invalid_cpf') {
          setError('CPF inválido — confira os dígitos.');
        } else {
          setError(friendlyMessage(err, 'Falha ao salvar perfil'));
        }
      } else {
        setError('Não foi possível conectar ao servidor');
      }
      setSubmitting(false);
    }
  }

  function onLogout() {
    useAuthStore.getState().logout();
    navigate('/login', { replace: true });
  }

  // NSS (design/no-scroll/nss-auth.jsx · CompleteProfileNS): viewport fixa,
  // corpo rolável (§5.2), CTA ancorado. O consentimento de marketing
  // (opcional) é o primeiro a sair quando falta altura.
  return (
    <div
      className="mx-auto flex max-w-sm flex-col overflow-hidden px-7"
      style={{
        height: kbOpen && vvHeight !== null ? `${vvHeight}px` : '100dvh',
        paddingTop: kbOpen ? 10 : 'max(1.75rem, env(safe-area-inset-top))',
        paddingBottom: kbOpen ? 10 : 'max(1.5rem, env(safe-area-inset-bottom))'
      }}
    >
      <div className="flex-shrink-0">
        <div
          className={`flex items-center gap-2.5 ${
            kbOpen ? 'mb-2' : 'mb-6 [@media(max-height:740px)]:mb-3'
          }`}
        >
          <BowArc size={kbOpen ? 24 : 32} />
          <span className="text-lg font-semibold tracking-tighter">Bow</span>
        </div>

        {kbOpen ? (
          <Display size={20} className="mb-2">
            Complete seu cadastro.
          </Display>
        ) : (
          <>
            <Eyebrow>Última etapa</Eyebrow>
            <Display
              size={30}
              className="mt-2.5 [@media(max-height:740px)]:mt-1.5 [@media(max-height:740px)]:!text-[24px]"
            >
              Complete seu cadastro.
            </Display>
            <p className="mt-2 text-sm leading-relaxed text-bow-royal/60 [@media(max-height:740px)]:hidden">
              Precisamos do seu CPF e celular pra liberar PIX e recargas. Informações
              exigidas pela LGPD e pelo PagBank.
            </p>

            <div className="mt-5 flex gap-1.5 [@media(max-height:740px)]:mt-3">
              <span className="h-1 flex-1 rounded-full bg-bow-royal" />
              <span className="h-1 flex-1 rounded-full bg-bow-royal" />
              <span className="h-1 flex-1 rounded-full bg-bow-royal/10" />
            </div>
          </>
        )}
      </div>

      {/* Corpo — rola quando não cabe (fonte grande / teclado aberto) */}
      <div
        onFocusCapture={revealFocusedField}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
      {user?.email && (
        <Card className="mt-6 flex items-baseline justify-between p-4">
          <Eyebrow>E-mail</Eyebrow>
          <span className="text-sm font-semibold">{user.email}</span>
        </Card>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {/* autoComplete="off" + name único pra desligar o autofill do browser
            (Samsung/Chrome guardam dados de formulários e sugerem). */}
        <Input
          label="Celular"
          type="tel"
          inputMode="numeric"
          autoComplete="off"
          name="bow-phone"
          placeholder="(27) 99274-3433"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
        />
        <Input
          label="CPF"
          inputMode="numeric"
          autoComplete="off"
          name="bow-cpf"
          placeholder="000.000.000-00"
          value={cpf}
          onChange={(e) => setCpf(formatCPF(e.target.value))}
        />
      </div>

      <div className="mt-5 flex flex-col gap-2 text-[13px] leading-snug text-bow-royal/70">
        <Consent
          checked={consentTerms}
          onChange={setConsentTerms}
        >
          Li e aceito os{' '}
          <a
            href="https://bow.app.br/privacidade#termos"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline underline-offset-2"
          >
            termos de uso
          </a>
        </Consent>
        <Consent
          checked={consentPrivacy}
          onChange={setConsentPrivacy}
        >
          Li e aceito a{' '}
          <a
            href="https://bow.app.br/privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline underline-offset-2"
          >
            política de privacidade
          </a>
        </Consent>
        {/* Opcional — primeiro a sair quando falta altura (mock NSS) */}
        <div className="[@media(max-height:740px)]:hidden">
          <Consent
            checked={consentMarketing}
            onChange={setConsentMarketing}
          >
            Aceito receber comunicações promocionais (opcional)
          </Consent>
        </div>
      </div>

      {error && <p className="mt-3 px-1 text-sm text-bow-red">{error}</p>}

      {!canSubmit && disabledReasons.length > 0 && (
        <p className="mt-3 px-1 text-xs text-bow-royal/60">
          Falta: {disabledReasons.join(', ')}
        </p>
      )}
      </div>

      {/* Rodapé ancorado */}
      <div className="flex flex-shrink-0 flex-col gap-1.5 pt-3">
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          onPointerDown={(e) => e.preventDefault()}
        >
          {submitting ? 'Salvando…' : 'Continuar'}
        </Button>
        <button
          type="button"
          onClick={onLogout}
          className={`h-10 text-[13px] font-medium text-bow-royal/55 underline-offset-2 hover:underline ${
            kbOpen ? 'hidden' : ''
          }`}
        >
          Sair e entrar com outra conta
        </button>
      </div>
    </div>
  );
}

interface ConsentProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}

function Consent({ checked, onChange, children }: ConsentProps) {
  return (
    <label className="flex cursor-pointer items-start gap-3 py-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span
        aria-hidden
        className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-[1.5px] transition ${
          checked
            ? 'border-bow-royal bg-bow-royal text-bow-cream'
            : 'border-bow-royal/40 bg-white text-transparent'
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-3 w-3"
        >
          <path d="m5 12 5 5L20 7" />
        </svg>
      </span>
      <span>{children}</span>
    </label>
  );
}
